/**
 * Selects the Agents client. Live (the real agent-trust endpoints) when VITE_AGENTS_LIVE=true
 * and an authed workspace is available; otherwise the fixture client — the default, so the
 * merged demo and the in-flight PR keep rendering the typed fixtures until live data is ready.
 *
 * `scope` is folded into the query keys (workspace id when live, "fixtures" otherwise) so
 * switching workspaces refetches and the two data sources never share a cache entry.
 */
import { useMemo } from "react";
import { useSession } from "@/features/auth/hooks/useSession";
import { resolveWorkspaceId } from "@/lib/workspace-routing";
import { agentsClient, type AgentsClient } from "./agentsClient";
import { createLiveAgentsClient } from "./liveAgentsClient";

const LIVE = import.meta.env.VITE_AGENTS_LIVE === "true";

export function useAgentsClient(): { client: AgentsClient; scope: string } {
  const { token, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  return useMemo(() => {
    if (LIVE && token && workspaceId) {
      return {
        client: createLiveAgentsClient(token, workspaceId),
        scope: workspaceId,
      };
    }
    return { client: agentsClient, scope: "fixtures" };
  }, [token, workspaceId]);
}
